import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { authService } from '../services/authService';
import api from '../services/api';
import Button from '../components/Button';
import Layout from '../components/Layout';
import './CrawlDetails.css';

// GEO Recommendations Component
function GEORecommendations({ crawlId, orgId }) {
  const [recommendations, setRecommendations] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [regenerating, setRegenerating] = useState(false);

  useEffect(() => {
    loadRecommendations();
    // Only load once when component mounts or crawlId/orgId changes
    // No auto-refresh to prevent infinite API calls
  }, [crawlId, orgId]);

  const loadRecommendations = async () => {
    try {
      setError(null);
      // Only set loading on first load, not on subsequent calls
      if (recommendations === null) {
        setLoading(true);
      }
      
      const response = await api.get(`/crawls/${crawlId}/geo/recommendations?organizationId=${orgId}`);
      console.log('GEO recommendations response:', response.data);
      setRecommendations(response.data.recommendations);
    } catch (error) {
      console.error('Error loading GEO recommendations:', error);
      setError(error.response?.data?.error || error.message || 'Failed to load recommendations');
      setRecommendations(null);
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerate = async () => {
    try {
      setRegenerating(true);
      setError(null);
      
      // Clear cache and trigger regeneration
      await api.post(`/crawls/${crawlId}/geo/recommendations/regenerate?organizationId=${orgId}`);
      
      // Reload recommendations (will generate new ones since cache is cleared)
      setLoading(true);
      await loadRecommendations();
    } catch (error) {
      console.error('Error regenerating recommendations:', error);
      setError(error.response?.data?.error || error.message || 'Failed to regenerate recommendations');
    } finally {
      setRegenerating(false);
    }
  };

  if (loading) return <div className="loading-text">Loading recommendations...</div>;
  if (error) return <div className="error-text">Error: {error}</div>;
  if (!recommendations || (typeof recommendations === 'object' && Object.keys(recommendations).length === 0)) {
    return <div className="info-text">No recommendations available yet. Complete the crawl analysis to see improvement suggestions.</div>;
  }

  return (
    <div className="geo-recommendations">
      <div className="geo-recommendations-header">
        <h3>AI-Powered Improvement Recommendations</h3>
        <button
          className="regenerate-recommendations-btn"
          onClick={handleRegenerate}
          disabled={regenerating || loading}
          title="Regenerate recommendations (this will use OpenAI API credits)"
        >
          {regenerating ? '🔄 Regenerating...' : '🔄 Regenerate Recommendations'}
        </button>
      </div>
      {recommendations.overallAssessment && (
        <div className="geo-assessment">
          <p>{recommendations.overallAssessment}</p>
        </div>
      )}
      
      {recommendations.priorityActions && recommendations.priorityActions.length > 0 && (
        <div className="priority-actions">
          <h4>Priority Actions</h4>
          {recommendations.priorityActions.map((action, idx) => (
            <div key={idx} className="priority-action-card">
              <div className="action-header">
                <span className="action-category">{action.category}</span>
                <span className={`action-priority priority-${action.priority}`}>
                  {action.priority.toUpperCase()}
                </span>
              </div>
              <h5>{action.action}</h5>
              {action.suggestedContent && (
                <div className="action-content">
                  <strong>Suggested Content:</strong>
                  <div className="content-box">{action.suggestedContent}</div>
                </div>
              )}
              {action.implementation && (
                <div className="action-implementation">
                  <strong>Implementation Steps:</strong>
                  <ol>
                    {(() => {
                      const impl = action.implementation;
                      if (typeof impl === 'string') {
                        return impl.split('\n').filter(s => s.trim()).map((step, idx) => (
                          <li key={idx}>{step.trim()}</li>
                        ));
                      } else if (Array.isArray(impl)) {
                        return impl.map((step, idx) => (
                          <li key={idx}>{typeof step === 'string' ? step : JSON.stringify(step)}</li>
                        ));
                      } else {
                        return [<li key={0}>{String(impl)}</li>];
                      }
                    })()}
                  </ol>
                </div>
              )}
              {action.pages && action.pages.length > 0 && (
                <div className="action-pages">
                  <strong>Affected Pages:</strong>
                  <ul>
                    {action.pages.map((page, pIdx) => (
                      <li key={pIdx}>
                        <a href={page} target="_blank" rel="noopener noreferrer" className="page-link">
                          {page}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {action.impact && (
                <div className="action-impact">
                  <strong>Expected Impact:</strong> {action.impact}
                </div>
              )}
              {action.estimatedTime && (
                <div className="action-time">
                  <strong>Estimated Time:</strong> {action.estimatedTime}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      
      {recommendations.quickWins && recommendations.quickWins.length > 0 && (
        <div className="quick-wins">
          <h4>Quick Wins</h4>
          {recommendations.quickWins.map((win, idx) => (
            <div key={idx} className="quick-win-card">
              <h5>{win.action}</h5>
              {win.suggestedContent && (
                <div className="win-content">
                  <strong>Suggested Content:</strong>
                  <div className="content-box">{win.suggestedContent}</div>
                </div>
              )}
              {win.implementation && (
                <div className="win-implementation">
                  <strong>Implementation:</strong>
                  <ol>
                    {(() => {
                      const impl = win.implementation;
                      if (typeof impl === 'string') {
                        return impl.split('\n').filter(s => s.trim()).map((step, sIdx) => (
                          <li key={sIdx}>{step.trim()}</li>
                        ));
                      } else if (Array.isArray(impl)) {
                        return impl.map((step, sIdx) => (
                          <li key={sIdx}>{typeof step === 'string' ? step : JSON.stringify(step)}</li>
                        ));
                      } else {
                        return [<li key={0}>{String(impl)}</li>];
                      }
                    })()}
                  </ol>
                </div>
              )}
              {win.estimatedTime && (
                <div className="win-time">
                  <strong>Estimated Time:</strong> {win.estimatedTime}
                </div>
              )}
              {win.expectedImprovement && (
                <div className="win-improvement">
                  <strong>Expected Improvement:</strong> {win.expectedImprovement}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      
      {recommendations.pageSpecificRecommendations && recommendations.pageSpecificRecommendations.length > 0 && (
        <div className="page-specific-recommendations">
          <h4>Page-Specific Recommendations</h4>
          {recommendations.pageSpecificRecommendations.map((pageRec, idx) => (
            <div key={idx} className="page-recommendation-card">
              <div className="page-rec-header">
                <h5>
                  <a href={pageRec.pageUrl} target="_blank" rel="noopener noreferrer" className="page-link">
                    {pageRec.pageUrl}
                  </a>
                </h5>
                <div className="page-rec-meta">
                  <span className="page-type-badge">{pageRec.pageType || 'Page'}</span>
                  <span className="score-indicator">
                    Current: {pageRec.currentScore || 0}/100 → Target: {pageRec.targetScore || 70}/100
                  </span>
                </div>
              </div>
              {pageRec.recommendations && pageRec.recommendations.length > 0 && (
                <div className="page-rec-list">
                  {pageRec.recommendations.map((rec, rIdx) => (
                    <div key={rIdx} className="page-rec-item">
                      <div className="rec-factor-badge">{rec.factor}</div>
                      <div className="rec-action">{rec.action}</div>
                      {rec.suggestedContent && (
                        <div className="rec-content">
                          <strong>Suggested Content:</strong>
                          <div className="content-box">{rec.suggestedContent}</div>
                        </div>
                      )}
                      {rec.implementation && (
                        <div className="rec-implementation">
                          <strong>Implementation:</strong>
                          <ol>
                            {(() => {
                              const impl = rec.implementation;
                              if (typeof impl === 'string') {
                                return impl.split('\n').filter(s => s.trim()).map((step, sIdx) => (
                                  <li key={sIdx}>{step.trim()}</li>
                                ));
                              } else if (Array.isArray(impl)) {
                                return impl.map((step, sIdx) => (
                                  <li key={sIdx}>{typeof step === 'string' ? step : JSON.stringify(step)}</li>
                                ));
                              } else {
                                return [<li key={0}>{String(impl)}</li>];
                              }
                            })()}
                          </ol>
                        </div>
                      )}
                      {rec.expectedImprovement && (
                        <div className="rec-improvement">
                          <strong>Expected Improvement:</strong> {rec.expectedImprovement}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      
      {recommendations.longTermStrategy && (
        <div className="long-term-strategy">
          <h4>Long-Term Strategy</h4>
          <p>{recommendations.longTermStrategy}</p>
        </div>
      )}
    </div>
  );
}

function CrawlDetails() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const orgId = searchParams.get('orgId');
  const navigate = useNavigate();

  const [crawl, setCrawl] = useState(null);
  const [issues, setIssues] = useState([]);
  const [geoData, setGeoData] = useState(null);
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [issueFilters, setIssueFilters] = useState({ severity: '', limit: 50 });
  const [metrics, setMetrics] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [issuesPerPage] = useState(20);
  const [aiProgress, setAiProgress] = useState(null);
  const [isGeneratingAI, setIsGeneratingAI] = useState(false);

  useEffect(() => {
    if (!authService.isAuthenticated()) {
      navigate('/login');
      return;
    }

    if (!orgId) {
      navigate('/dashboard');
      return;
    }

    loadCrawlData();
    setCurrentPage(1); // Reset to first page when filters change
  }, [id, orgId, navigate, issueFilters.severity, issueFilters.limit]);

  const loadCrawlData = async () => {
    try {
      // Load crawl status
      const statusResponse = await api.get(`/crawls/${id}/status?organizationId=${orgId}`);
      setCrawl(statusResponse.data.crawl);

      // Load issues
      const issuesResponse = await api.get(`/crawls/${id}/issues?organizationId=${orgId}&limit=${issueFilters.limit}${issueFilters.severity ? `&severity=${issueFilters.severity}` : ''}`);
      setIssues(issuesResponse.data.issues || []);

      // Load GEO scores
      try {
        const geoResponse = await api.get(`/crawls/${id}/geo?organizationId=${orgId}`);
        setGeoData(geoResponse.data);
      } catch (err) {
        console.error('Error loading GEO scores:', err);
      }


      // Load pages
      try {
        const pagesResponse = await api.get(`/crawls/${id}/pages?organizationId=${orgId}&limit=10`);
        setPages(pagesResponse.data.pages || []);
      } catch (err) {
        console.error('Error loading pages:', err);
      }

      // Calculate metrics
      const issuesWithSolutions = issues.filter(i => i.aiRecommendation).length;
      const topIssueTypes = {};
      issues.forEach(issue => {
        topIssueTypes[issue.issueCode] = (topIssueTypes[issue.issueCode] || 0) + 1;
      });
      const sortedIssueTypes = Object.entries(topIssueTypes)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5);
      
      const pagesWithIssuesSet = new Set(issues.map(i => i.pageId).filter(id => id));
      const pagesWithIssues = pagesWithIssuesSet.size;
      const pagesWithoutIssues = Math.max(0, (crawl.pageCount || 0) - pagesWithIssues);
      
      setMetrics({
        issuesWithSolutions,
        solutionCoverage: issues.length > 0 ? Math.round((issuesWithSolutions / issues.length) * 100) : 0,
        topIssueTypes: sortedIssueTypes,
        pagesWithIssues,
        pagesWithoutIssues
      });
    } catch (error) {
      console.error('Error loading crawl data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return '#dc2626';
      case 'high': return '#ea580c';
      case 'medium': return '#d97706';
      case 'low': return '#65a30d';
      default: return '#6b7280';
    }
  };

  const getSeverityCount = (severity) => {
    return issues.filter(i => i.severity === severity).length;
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading crawl details...</p>
      </div>
    );
  }

  if (!crawl) {
    return (
      <div className="error-container">
        <h2>Crawl not found</h2>
        <Button onClick={() => navigate('/dashboard')}>Back to Dashboard</Button>
      </div>
    );
  }

  return (
    <Layout>
      <div className="crawl-details-page">
        <div className="page-header">
          <div>
            <Button variant="ghost" onClick={() => navigate(`/project/${crawl.projectId}?orgId=${orgId}`)} size="small">
              ← Back
            </Button>
            <h1 className="page-title">Crawl #{crawl.id}</h1>
            <p className="page-subtitle">{crawl.rootUrl}</p>
          </div>
          <div className="header-stats">
            <div className="stat-item">
              <span className="stat-label">Status</span>
              <span className={`status-badge status-${crawl.status?.toLowerCase() || 'pending'}`}>
                {(crawl.status || 'PENDING').toUpperCase()}
              </span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Pages</span>
              <span className="stat-value">{crawl.pageCount || 0}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Issues</span>
              <span className="stat-value">{crawl.issueCount || 0}</span>
            </div>
          </div>
        </div>

        <div className="crawl-content">
          <div className="crawl-main">
            <div className="tabs">
          <button
            className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            Overview
          </button>
          <button
            className={`tab ${activeTab === 'issues' ? 'active' : ''}`}
            onClick={() => setActiveTab('issues')}
          >
            Issues ({issues.length})
          </button>
          <button
            className={`tab ${activeTab === 'geo' ? 'active' : ''}`}
            onClick={() => setActiveTab('geo')}
          >
            E-E-A-T Score
          </button>
          <button
            className={`tab ${activeTab === 'pages' ? 'active' : ''}`}
            onClick={() => setActiveTab('pages')}
          >
            Pages ({pages.length})
          </button>
        </div>

        <div className="tab-content">
          {activeTab === 'overview' && (
            <div className="overview-tab">
              <div className="overview-grid">
                <div className="overview-card">
                  <h3>Crawl Summary</h3>
                  <div className="summary-item">
                    <span className="summary-label">Started:</span>
                    <span className="summary-value">
                      {crawl.startedAt ? new Date(crawl.startedAt).toLocaleString() : 'N/A'}
                    </span>
                  </div>
                  <div className="summary-item">
                    <span className="summary-label">Completed:</span>
                    <span className="summary-value">
                      {crawl.finishedAt ? new Date(crawl.finishedAt).toLocaleString() : 'In Progress'}
                    </span>
                  </div>
                  <div className="summary-item">
                    <span className="summary-label">Total Pages:</span>
                    <span className="summary-value">{crawl.pageCount || 0}</span>
                  </div>
                  <div className="summary-item">
                    <span className="summary-label">Total Issues:</span>
                    <span className="summary-value">{crawl.issueCount || 0}</span>
                  </div>
                </div>

                <div className="overview-card">
                  <h3>Issues by Severity</h3>
                  <div className="severity-breakdown">
                    <div className="severity-item">
                      <span className="severity-dot" style={{ backgroundColor: getSeverityColor('critical') }}></span>
                      <span className="severity-label">Critical:</span>
                      <span className="severity-count">{getSeverityCount('critical')}</span>
                    </div>
                    <div className="severity-item">
                      <span className="severity-dot" style={{ backgroundColor: getSeverityColor('high') }}></span>
                      <span className="severity-label">High:</span>
                      <span className="severity-count">{getSeverityCount('high')}</span>
                    </div>
                    <div className="severity-item">
                      <span className="severity-dot" style={{ backgroundColor: getSeverityColor('medium') }}></span>
                      <span className="severity-label">Medium:</span>
                      <span className="severity-count">{getSeverityCount('medium')}</span>
                    </div>
                    <div className="severity-item">
                      <span className="severity-dot" style={{ backgroundColor: getSeverityColor('low') }}></span>
                      <span className="severity-label">Low:</span>
                      <span className="severity-count">{getSeverityCount('low')}</span>
                    </div>
                  </div>
                </div>

                {geoData && (
                  <div className="overview-card">
                    <h3>GEO Score</h3>
                    <div className="geo-score-display">
                      <div className="geo-score-circle">
                        <span className="geo-score-value">{geoData.overallScore || 0}</span>
                        <span className="geo-score-label">/ 100</span>
                      </div>
                      {geoData.breakdown && (
                        <div className="geo-breakdown">
                          {Object.entries(geoData.breakdown).map(([key, value]) => (
                            <div key={key} className="geo-breakdown-item">
                              <span className="geo-breakdown-label">{key.replace(/_/g, ' ')}:</span>
                              <span className="geo-breakdown-value">{value}%</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {metrics && (
                  <div className="overview-card">
                    <h3>Solution Coverage</h3>
                    <div className="solution-coverage">
                      <div className="coverage-circle">
                        <span className="coverage-value">{metrics.solutionCoverage}%</span>
                        <span className="coverage-label">Issues with Solutions</span>
                      </div>
                      <div className="coverage-stats">
                        <div className="coverage-stat">
                          <span className="coverage-stat-value">{metrics.issuesWithSolutions}</span>
                          <span className="coverage-stat-label">Issues with AI Solutions</span>
                        </div>
                        <div className="coverage-stat">
                          <span className="coverage-stat-value">{issues.length - metrics.issuesWithSolutions}</span>
                          <span className="coverage-stat-label">Issues Pending Solutions</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {metrics && metrics.topIssueTypes.length > 0 && (
                  <div className="overview-card">
                    <h3>Top Issue Types</h3>
                    <div className="top-issues">
                      {metrics.topIssueTypes.map(([issueCode, count], idx) => (
                        <div key={idx} className="top-issue-item">
                          <span className="top-issue-code">{issueCode.replace(/_/g, ' ')}</span>
                          <span className="top-issue-count">{count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {metrics && (
                  <div className="overview-card">
                    <h3>Page Health</h3>
                    <div className="page-health">
                      <div className="health-stat">
                        <span className="health-stat-value">{metrics.pagesWithIssues}</span>
                        <span className="health-stat-label">Pages with Issues</span>
                      </div>
                      <div className="health-stat">
                        <span className="health-stat-value">{Math.max(0, metrics.pagesWithoutIssues)}</span>
                        <span className="health-stat-label">Pages without Issues</span>
                      </div>
                      <div className="health-stat">
                        <span className="health-stat-value">{crawl.pageCount || 0}</span>
                        <span className="health-stat-label">Total Pages</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'issues' && (
            <div className="issues-tab">
              <div className="issues-filters">
                <select
                  value={issueFilters.severity}
                  onChange={(e) => setIssueFilters({ ...issueFilters, severity: e.target.value })}
                  className="filter-select"
                >
                  <option value="">All Severities</option>
                  <option value="critical">Critical</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
                <button
                  className="generate-ai-btn"
                  onClick={async () => {
                    try {
                      setIsGeneratingAI(true);
                      setAiProgress({ status: 'queued', progress: 0, recommendationsCount: 0 });
                      const response = await api.post(`/crawls/${id}/generate-ai?organizationId=${orgId}`);
                      
                      // Start polling for progress
                      const progressInterval = setInterval(async () => {
                        try {
                          const progressResponse = await api.get(`/crawls/${id}/ai/progress?organizationId=${orgId}`);
                          setAiProgress(progressResponse.data);
                          
                          if (progressResponse.data.status === 'completed' || progressResponse.data.status === 'failed') {
                            clearInterval(progressInterval);
                            setIsGeneratingAI(false);
                            // Reload issues to show new recommendations
                            setTimeout(() => {
                              loadCrawlData();
                            }, 1000);
                          }
                        } catch (err) {
                          console.error('Error checking AI progress:', err);
                        }
                      }, 2000); // Poll every 2 seconds
                      
                      // Stop polling after 5 minutes
                      setTimeout(() => {
                        clearInterval(progressInterval);
                        setIsGeneratingAI(false);
                      }, 300000);
                    } catch (error) {
                      setIsGeneratingAI(false);
                      alert('Failed to generate AI recommendations: ' + (error.response?.data?.error || error.message));
                    }
                  }}
                  disabled={isGeneratingAI}
                >
                  {isGeneratingAI ? '🔄 Generating...' : '🤖 Generate AI Solutions'}
                </button>
                {aiProgress && isGeneratingAI && (
                  <div className="ai-progress-indicator">
                    <div className="ai-progress-bar">
                      <div 
                        className="ai-progress-fill" 
                        style={{ width: `${aiProgress.progress || 0}%` }}
                      ></div>
                    </div>
                    <div className="ai-progress-text">
                      {aiProgress.status === 'active' ? (
                        <>Generating recommendations... {aiProgress.progress || 0}% ({aiProgress.recommendationsCount || 0} generated)</>
                      ) : aiProgress.status === 'completed' ? (
                        <>Completed! {aiProgress.recommendationsCount || 0} recommendations generated</>
                      ) : (
                        <>Queued... Waiting to start</>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="issues-list">
                {issues.length === 0 ? (
                  <div className="empty-state">
                    <p>No issues found</p>
                  </div>
                ) : (() => {
                  const startIndex = (currentPage - 1) * issuesPerPage;
                  const endIndex = startIndex + issuesPerPage;
                  const paginatedIssues = issues.slice(startIndex, endIndex);
                  const totalPages = Math.ceil(issues.length / issuesPerPage);
                  
                  return (
                    <>
                      <div className="issues-count-header">
                        Showing {startIndex + 1}-{Math.min(endIndex, issues.length)} of {issues.length} issues
                      </div>
                      {paginatedIssues.map((issue) => (
                    <div key={issue.id} className="issue-card">
                      <div className="issue-header">
                        <span
                          className="severity-badge"
                          style={{ backgroundColor: getSeverityColor(issue.severity) }}
                        >
                          {issue.severity.toUpperCase()}
                        </span>
                        <div className="issue-header-content">
                          <h4>{issue.title}</h4>
                          {issue.pageUrl && (
                            <a href={issue.pageUrl} target="_blank" rel="noopener noreferrer" className="issue-page-link">
                              {issue.pageUrl}
                            </a>
                          )}
                        </div>
                      </div>
                      <p className="issue-description">{issue.description}</p>
                      
                      {issue.aiRecommendation ? (
                        <div className="ai-solution">
                          <div className="ai-solution-header">
                            <span className="ai-badge">🤖 AI Solution</span>
                          </div>
                          {issue.aiRecommendation.solution && (
                            <>
                              <div className="solution-action">
                                <strong>Action:</strong> {issue.aiRecommendation.solution.action}
                              </div>
                              {issue.aiRecommendation.solution.suggestedContent && (
                                <div className="solution-content">
                                  <strong>Suggested Content:</strong>
                                  <div className="content-box">
                                    {issue.aiRecommendation.solution.suggestedContent}
                                  </div>
                                </div>
                              )}
                              {issue.aiRecommendation.solution.implementation && (
                                <div className="solution-steps">
                                  <strong>Implementation Steps:</strong>
                                  <ol>
                                    {(() => {
                                      const impl = issue.aiRecommendation.solution.implementation;
                                      if (typeof impl === 'string') {
                                        return impl.split('\n').filter(s => s.trim()).map((step, idx) => (
                                          <li key={idx}>{step.trim()}</li>
                                        ));
                                      } else if (Array.isArray(impl)) {
                                        return impl.map((step, idx) => (
                                          <li key={idx}>{typeof step === 'string' ? step : JSON.stringify(step)}</li>
                                        ));
                                      } else {
                                        return [<li key={0}>{String(impl)}</li>];
                                      }
                                    })()}
                                  </ol>
                                </div>
                              )}
                              {issue.aiRecommendation.solution.placement && (
                                <div className="solution-placement">
                                  <strong>Where to add:</strong> {issue.aiRecommendation.solution.placement}
                                </div>
                              )}
                              {issue.aiRecommendation.whyItMatters && (
                                <div className="solution-impact">
                                  <strong>Why this matters:</strong> {issue.aiRecommendation.whyItMatters}
                                </div>
                              )}
                              {issue.aiRecommendation.expectedImpact && (
                                <div className="solution-impact-value">
                                  <strong>Expected Impact:</strong> {issue.aiRecommendation.expectedImpact}
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      ) : issue.fixSuggestion ? (
                        <div className="issue-fix">
                          <strong>Fix:</strong> {issue.fixSuggestion}
                        </div>
                      ) : null}
                      
                      {issue.evidence && Object.keys(issue.evidence).length > 0 && (
                        <div className="issue-evidence">
                          <strong>Evidence:</strong>
                          <pre className="evidence-json">
                            {typeof issue.evidence === 'string' 
                              ? issue.evidence 
                              : JSON.stringify(issue.evidence, null, 2)}
                          </pre>
                        </div>
                      )}
                    </div>
                      ))}
                      
                      {totalPages > 1 && (
                        <div className="pagination">
                          <button
                            className="pagination-btn"
                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                            disabled={currentPage === 1}
                          >
                            ← Previous
                          </button>
                          <span className="pagination-info">
                            Page {currentPage} of {totalPages} ({issues.length} total issues)
                          </span>
                          <button
                            className="pagination-btn"
                            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                            disabled={currentPage === totalPages}
                          >
                            Next →
                          </button>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>
          )}

          {activeTab === 'geo' && (
            <div className="geo-tab">
              {geoData ? (
                <div className="geo-content-modern">
                  <div className="geo-header-section">
                    <h2>E-E-A-T Readiness Score</h2>
                    <div className="geo-score-display-modern">
                      <div className="geo-score-circle-modern">
                        <div className="geo-score-inner">
                          <span className="geo-score-value-modern">{geoData.overallScore || 0}</span>
                          <span className="geo-score-max">/ 100</span>
                        </div>
                        <div className="geo-score-ring" style={{
                          background: `conic-gradient(
                            ${geoData.overallScore >= 80 ? '#10b981' : geoData.overallScore >= 60 ? '#f59e0b' : '#ef4444'} 0deg,
                            ${geoData.overallScore >= 80 ? '#10b981' : geoData.overallScore >= 60 ? '#f59e0b' : '#ef4444'} ${(geoData.overallScore || 0) * 3.6}deg,
                            #e5e7eb ${(geoData.overallScore || 0) * 3.6}deg,
                            #e5e7eb 360deg
                          )`
                        }}></div>
                      </div>
                      <div className="geo-score-status">
                        {geoData.overallScore >= 80 ? (
                          <span className="status-badge-good">Excellent</span>
                        ) : geoData.overallScore >= 60 ? (
                          <span className="status-badge-medium">Good</span>
                        ) : (
                          <span className="status-badge-poor">Needs Improvement</span>
                        )}
                      </div>
                    </div>
                    <div className="geo-explanation-modern">
                      <p>
                        <strong>E-E-A-T</strong> stands for <strong>Experience</strong>, <strong>Expertise</strong>, 
                        <strong>Authoritativeness</strong>, and <strong>Trustworthiness</strong>. Google uses this framework 
                        to evaluate content quality. Higher scores indicate better content quality and improved search rankings.
                      </p>
                    </div>
                  </div>

                  {geoData.breakdown && (
                    <div className="geo-breakdown-modern">
                      <h3>Score Breakdown</h3>
                      <div className="breakdown-grid">
                        {Object.entries(geoData.breakdown).map(([key, value]) => {
                          const labels = {
                            entity: { name: 'Entity/Author', desc: 'Author expertise and credentials' },
                            schema: { name: 'Schema Markup', desc: 'Structured data for search engines' },
                            structure: { name: 'Content Structure', desc: 'Proper headings and organization' },
                            citability: { name: 'Citability', desc: 'External references and citations' },
                            completeness: { name: 'Completeness', desc: 'Comprehensive, detailed content' }
                          };
                          const label = labels[key.toLowerCase()] || { name: key.replace(/_/g, ' '), desc: 'Score component' };
                          const scoreColor = value >= 70 ? '#10b981' : value >= 40 ? '#f59e0b' : '#ef4444';
                          
                          return (
                            <div key={key} className="breakdown-card">
                              <div className="breakdown-card-header">
                                <h4>{label.name}</h4>
                                <span className="breakdown-score" style={{ color: scoreColor }}>
                                  {value}%
                                </span>
                              </div>
                              <p className="breakdown-desc">{label.desc}</p>
                              <div className="breakdown-progress-bar">
                                <div 
                                  className="breakdown-progress-fill" 
                                  style={{ 
                                    width: `${value}%`,
                                    backgroundColor: scoreColor
                                  }}
                                ></div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  
                  {/* GEO Improvement Recommendations */}
                  <GEORecommendations crawlId={id} orgId={orgId} />
                </div>
              ) : (
                <div className="empty-state">
                  <p>E-E-A-T scores not available yet. Complete the crawl analysis to see your scores.</p>
                </div>
              )}
            </div>
          )}


          {activeTab === 'pages' && (
            <div className="pages-tab">
              <div className="pages-intro">
                <h3>Crawled Pages</h3>
                <p>These are all the pages we found and analyzed during the crawl. Click on any page to see detailed issues and recommendations.</p>
              </div>
              <div className="pages-list">
                {pages.length === 0 ? (
                  <div className="empty-state">
                    <p>No pages found</p>
                  </div>
                ) : (
                  pages.map((page) => (
                    <div key={page.id} className="page-card">
                      <div className="page-header">
                        <h4>{page.title || 'Untitled Page'}</h4>
                        <span className={`status-code status-${Math.floor((page.statusCode || 200) / 100)}xx`}>
                          {page.statusCode || 'N/A'}
                        </span>
                      </div>
                      <a href={page.url} target="_blank" rel="noopener noreferrer" className="page-url">
                        {page.url}
                      </a>
                      <div className="page-meta-info">
                        {page.wordCount && (
                          <span className="page-meta">📝 {page.wordCount} words</span>
                        )}
                        {page.h1 && (
                          <span className="page-meta">✓ Has H1</span>
                        )}
                        {page.metaDescription && (
                          <span className="page-meta">✓ Has Meta Description</span>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default CrawlDetails;

